import { useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import RouteNames from "../../routes/type";
import { BackButton, Button } from "../../components";
import { classNames, navigateTo, YesNoToBoolean } from "../../helper";
import { claimStepsData } from "./types";
import { PiCheckBold } from "react-icons/pi";
import { FormProvider, useForm } from "react-hook-form";
import queryString from "query-string";
import { useTranslation } from "react-i18next";
import CustomerInfo from "./CustomerInfo";
import PurchaseInfo from "./PurchaseInfo";
import DamageDetails from "./DamageDetails";
import UploadDocuments from "./UploadDocuments";
import ReviewDetails from "./ReviewDetails";
import SubmitClaim from "./SubmitClaim";
import { connect, useDispatch } from "react-redux";
import {
  cleanUpClaim,
  fetchClaimInfo,
  submitClaim,
  upsertClaim,
} from "../../store/mattress-claim/actions";
import { RootState } from "../../store/root-reducer";
import { ClaimScope } from "../../store/mattress-claim/types";
import { isScopeLoading } from "../../store/scope-loading/helpers";
import Loader from "../../components/Loader";
import LinkNotValid from "../../components/LinkNotValid";
import { getScopeError } from "../../store/scope-error/helpers";

function CreateClaim({ loading, error }) {
  let [searchParams] = useSearchParams();
  const formMethods = useForm({});
  const { handleSubmit, getValues } = formMethods;
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const stepId: number = parseInt(searchParams.get("step") || "");
  const claimId: string = searchParams.get("claimId") || "";

  useEffect(() => {
    if ((!stepId && !/^[0-9]*$/.test(`${stepId}`)) || stepId > 6 || !claimId) {
      const parsed = queryString.parse(window.location.search);
      navigate(
        `/${RouteNames.CREATE_CLAIM}?${queryString.stringify({
          ...parsed,
          step: 1,
        })}`,
        { replace: true }
      );
    }
    return () => {};
  }, [claimId, navigate, stepId]);

  useEffect(() => {
    if (claimId) {
      dispatch(fetchClaimInfo(claimId));
    }
    return () => {};
  }, [claimId, dispatch]);

  const currentStep = claimStepsData.find((item) => item.step_num === stepId);

  const navigateToStep = (step) => {
    const parsed = queryString.parse(window.location.search);
    window.scrollTo(0, 0);
    navigateTo(
      `/${RouteNames.CREATE_CLAIM}?${queryString.stringify({
        ...parsed,
        step: step ?? stepId + 1,
      })}`
    );
  };

  const handleNext = () => {
    const values = getValues();
    switch (stepId) {
      case 1:
        const customerInfo = values?.customerInfo || {};
        dispatch(
          upsertClaim(1, {
            first_name: customerInfo.first_name || "",
            last_name: customerInfo.last_name || "",
            Email: customerInfo.email || "",
            phone_number: customerInfo.number || "",
            address_1: customerInfo.address1 || "",
            address_2: customerInfo.address2 || "",
            city: customerInfo.city || "",
            state: customerInfo.state || "",
            zip: customerInfo.zip || "",
            allow_sms: !!customerInfo.allowSMS,
          })
        );
        break;

      case 2:
        const purchaseInfo = values?.purchaseInfo || {};
        dispatch(
          upsertClaim(2, {
            store_of_purchase: purchaseInfo.store_purchase || "",
            retailer_phone: purchaseInfo.retailer_phone || "",
            retailer_address_1: purchaseInfo.address1 || "",
            retailer_address_2: purchaseInfo.address2 || "",
            retailer_city: purchaseInfo.city || "",
            retailer_state: purchaseInfo.state || "",
            retailer_zip: purchaseInfo.zip || "",
            purchase_date: purchaseInfo.purchase_date || "",
          })
        );
        break;

      case 3:
        const damageInfo = values?.damageInfo || {};
        dispatch(
          upsertClaim(3, {
            date_of_incident: damageInfo.date_incident,
            protector_encasement_type: damageInfo.product_type,
            protector_encasement_size: damageInfo.product_size,
            mattress_stained_damaged: YesNoToBoolean(
              damageInfo.mattress_stained_damaged
            ),
            type_of_stain_damage: damageInfo.type_of_stain_damage,
            protector_encasement_stained_damaged: YesNoToBoolean(
              damageInfo.protector_damage
            ),
            damage_notes: damageInfo.damageNotes,
            type_of_stain_damage_protector: damageInfo.damage_Protector_type,
            estimated_mattress_retail_value: damageInfo.estimated_retail_value,
            did_you_clean_the_protector_encasement: YesNoToBoolean(
              damageInfo.did_clean_product
            ),
            encasement_on_your_bed_at_the_time_of_the_incident: YesNoToBoolean(
              damageInfo.protector_on_bed
            ),
          })
        );
        break;

      case 4:
        navigateToStep(5);
        break;

      case 5:
        dispatch(submitClaim());
        break;

      default:
        break;
    }
  };

  useEffect(() => {
    const handleBeforeUnload = (event) => {
      event.preventDefault();
      event.returnValue = true;
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, []);

  if (error === "CLAIM_NOT_FOUND") {
    return <LinkNotValid />;
  }

  if (stepId === 6) {
    return (
      <Loader isLoading={loading}>
        <SubmitClaim key="stepId6" />
      </Loader>
    );
  }

  return (
    <div className="px-4 space-y-3">
      <div className="mt-4 flex justify-between items-center">
        <BackButton
          onClick={() => {
            if (stepId === 1) {
              navigate("/");
              dispatch(cleanUpClaim());
            } else {
              navigateToStep(stepId - 1);
            }
          }}
          text={stepId === 1 ? "Home" : "Back"}
        />
        <div className="flex flex-row gap-2">
          {claimStepsData.map(({ step_num: step }) => {
            const isCompleted = step < stepId;
            const isDraft = step > stepId;
            return (
              <button
                disabled
                key={step}
                className={classNames(
                  "size-7 flex rounded-full justify-center items-center text-base",
                  isDraft
                    ? "bg-[#009CAA] bg-opacity-15 text-[#009CAA] "
                    : "bg-[#009CAA] text-white"
                )}
              >
                {isCompleted ? <PiCheckBold size={16} color="white" /> : step}
              </button>
            );
          })}
        </div>
      </div>
      <h3 className="text-2xl font-semibold">{t(currentStep?.title)}</h3>
      <form onSubmit={handleSubmit(handleNext)}>
        <Loader isLoading={loading} />
        <FormProvider {...formMethods}>
          {stepId === 1 && <CustomerInfo key="stepId1" />}
          {stepId === 2 && <PurchaseInfo key="stepId2" />}
          {stepId === 3 && <DamageDetails key="stepId3" />}
          {stepId === 4 && <UploadDocuments key="stepId4" />}
          {stepId === 5 && <ReviewDetails key="stepId4" />}
        </FormProvider>
        <Button className="my-6" submit isLoading={loading}>
          {stepId === 5 ? t("Submit") : t("Next")}
        </Button>
      </form>
    </div>
  );
}

const mapStateToProps = (state: RootState) => ({
  loading: isScopeLoading(state.scopeLoading, ClaimScope),
  error: getScopeError(state.scopeError, ClaimScope),
  claim: state?.claim || {},
});

export default connect(mapStateToProps)(CreateClaim);
